import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/api/axios";
import { useAuthStore } from "@/store/auth";
import { useToastHandler } from "@/hooks/useToastHandler";
import { ExerciseModelJsonResource } from "@/types/exerciseTypes";

const ERROR_MSG = "Failed to fetch tracked exercises";
const API_PATH = "/api/exercises/tracked";
const USE_QUERY_KEY = API_PATH;

export function useTrackedExercises() {
  const { token } = useAuthStore();
  const { handleError } = useToastHandler();

  return useQuery<ExerciseModelJsonResource[] | null>({
    enabled: !!token,
    queryKey: [USE_QUERY_KEY],
    //refetchInterval: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      try {
        const res = await api.get(API_PATH);

        if (import.meta.env.DEV) {
          console.log(
            `>>> React Hook Fetching .... [${USE_QUERY_KEY}]`,
            res.data
          );
        }
        if (!res || !Array.isArray(res.data)) {
          throw new Error(ERROR_MSG + "(Invalid Data)");
        }
        return res.data;
      } catch (err: unknown) {
        handleError(
          err instanceof Error ? err.message : ERROR_MSG,
          3000,
          err instanceof Error ? err.stack ?? "" : ""
        );

        //You should throw the error in your query function, not `return null`.
        // This way, React Query will set isError: true and your error UI will be shown.
        throw err;
      }
    },
  });
}

// invalidate cache:
export function useInvalidateTrackedExercises() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [USE_QUERY_KEY] });
  };
}

// refetch cache:
export function useRefetchTrackedExercises() {
  const { refetch } = useTrackedExercises();
  return refetch;
}
